import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation, Prompt } from "react-router-dom";
import { messageShow } from "../../common/api/actions";
import routes from "../api/routes";
import { toCamelCase } from "../../../setup/helpers";
import {
  Box,
  Toolbar,
  Button,
  TextField,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  AddCircleOutline as AddIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";

import { createClientConfig, startClientScheduler, stopClientScheduler } from "../api/actions/mutation";
import { getClientConfig } from "../api/actions/query";
import { updateClientConfig } from "../api/actions/mutation";
import DynamicField from "./DynamicField";
import DynamicFiltersConfig from "./DynamicFiltersConfig";
import "./style.css";

const DEFAULT_CATEGORIES = [
  { label: "General Info", key: "generalInfo" },
  { label: "Firebase", key: "firebase" },
  { label: "SendGrid", key: "sendgrid" },
  { label: "Yelp", key: "yelp" },
  { label: "AWS", key: "aws" },
  { label: "Google OAuth", key: "googleOauth" },
  { label: "Dynamic Filters", key: "dynamicFilters" },
  { label: "Others", key: "others" },
];

const CreateOrUpdate = ({
  _id: propsId,
  crmId,
  prefillFolderPath,
  prefillDbConnectionURL,
  isModal = false,
  onCancel,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const clientConfigId =
    propsId !== undefined ? propsId : location.state?._id || null;
  const isEditMode = Boolean(clientConfigId);
  const [activeTab, setActiveTab] = useState(0);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [originalKeys, setOriginalKeys] = useState(new Set());
  const [originalCategories, setOriginalCategories] = useState(new Set());
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [isDirty, setIsDirty] = useState(false);

  const [formData, setFormData] = useState({
    generalInfo: [
      { key: "clientId", value: "", isStatic: true, type: "string" },
      { key: "channel", value: "", isStatic: true, type: "string" },
      { key: "folderPath", value: prefillFolderPath || "", isStatic: true, type: "string" },
      {
        key: "dbConnectionURL",
        value: prefillDbConnectionURL || "",
        isStatic: true,
        type: "string",
      },
      { key: "apiLink", value: "", isStatic: true, type: "string" },
      { key: "timeZone", value: "UTC", isStatic: true, type: "string" },
    ],
    firebase: [
      { key: "type", value: "", isStatic: true, type: "string" },
      { key: "project_id", value: "", isStatic: true, type: "string" },
      { key: "private_key_id", value: "", isStatic: true, type: "string" },
      { key: "private_key", value: "", isStatic: true, type: "string" },
      { key: "client_email", value: "", isStatic: true, type: "string" },
      { key: "client_id", value: "", isStatic: true, type: "string" },
      { key: "auth_uri", value: "", isStatic: true, type: "string" },
      { key: "token_uri", value: "", isStatic: true, type: "string" },
      { key: "auth_provider_x509_cert_url", value: "", isStatic: true, type: "string" },
      { key: "client_x509_cert_url", value: "", isStatic: true, type: "string" },
      { key: "universe_domain", value: "", isStatic: true, type: "string" },
    ],
    sendgrid: [
      { key: "api_key", value: "", isStatic: true, type: "string" },
      { key: "batchSize", value: "", isStatic: true, type: "number" },
      { key: "dailyLimit", value: "", isStatic: true, type: "number" },
    ],
    yelp: [{ key: "api_key", value: "", isStatic: true, type: "string" }],
    aws: [
      { key: "bucketName", value: "", isStatic: true, type: "string" },
      { key: "region", value: "", isStatic: true, type: "string" },
      { key: "domainUrl", value: "", isStatic: true, type: "string" },
    ],
    googleOauth: [
      { key: "google_oauth_client_id", value: "", isStatic: true, type: "string" },
      { key: "google_oauth_client_secret", value: "", isStatic: true, type: "string" },
      { key: "google_oauth_redirect_uri", value: "", isStatic: true, type: "string" },
      { key: "seo_redirect_uris", value: [], isStatic: true, type: "array" },
    ],
    dynamicFilters: [],
    others: [
      {
        key: "canCampaignSchedulerEnable",
        value: false,
        isStatic: true,
        type: "boolean",
      },
      {
        key: "canTokenRefreshEnable",
        value: false,
        isStatic: true,
        type: "boolean",
      },
      {
        key: "campaignSchedulerType",
        value: "node",
        isStatic: true,
        type: "string",
      },
    ],
  });

  const [passwordVisibility, setPasswordVisibility] = useState({});

  useEffect(() => {
    if (isEditMode) {
      fetchClientConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  const fetchClientConfig = async () => {
    try {
      const { data } = await getClientConfig(clientConfigId);
      const config = data.data;
      if (!config) return;

      const transformObjectToFields = (obj) => {
        if (!obj || typeof obj !== "object") return [];
        return Object.entries(obj).map(([key, value]) => {
          let type = "string";
          if (typeof value === "boolean") type = "boolean";
          else if (typeof value === "number") type = "number";
          else if (typeof value === "string" && !isNaN(Date.parse(value)) && /^\d{4}-\d{2}-\d{2}/.test(value)) type = "date";
          else if (Array.isArray(value)) type = "array";
          else if (value && typeof value === "object") type = "object";

          let handledValue = value;
          if (type === "object") {
            handledValue = transformObjectToFields(value);
          }

          return { key, value: handledValue, type };
        });
      };

      const mapToDefaults = (category, dataObj, defaultKeys) => {
        const result = defaultKeys.map((def) => {
          let value =
            dataObj[def.key] !== undefined ? dataObj[def.key] : def.value;

          if (def.type === "array" && !Array.isArray(value)) {
            value = value ? [value] : [];
          }
          if (def.type === "boolean") {
            value = !!value;
          }
          if (def.type === "date" && value) {
            value = new Date(value).toISOString().split("T")[0];
          }

          return { ...def, value };
        });

        const existingKeys = new Set(defaultKeys.map((d) => d.key));
        if (dataObj && typeof dataObj === 'object') {
            Object.entries(dataObj).forEach(([key, value]) => {
              if (!existingKeys.has(key) && value !== null) {
                let type = "string";
                if (typeof value === "boolean") type = "boolean";
                else if (typeof value === "number") type = "number";
                else if (typeof value === "string" && !isNaN(Date.parse(value)) && /^\d{4}-\d{2}-\d{2}/.test(value)) type = "date";
                else if (Array.isArray(value)) type = "array";
                else if (value && typeof value === "object") type = "object";
    
                let handledValue = value;
                if (type === "object") {
                  handledValue = transformObjectToFields(value);
                }
                result.push({ key, value: handledValue, type });
              }
            });
        }
        return result;
      };

      const systemFields = ["_id", "crmId", "createdAt", "updatedAt", "__v"];

      const customCatKeys = new Set();
      const othersFields = [
        "canCampaignSchedulerEnable",
        "canTokenRefreshEnable",
        "campaignSchedulerType",
      ];

      // 1. Detect and add any unknown nested objects as new tabs
      const finalCategories = [...DEFAULT_CATEGORIES];
      Object.entries(config).forEach(([key, value]) => {
        if (
          value &&
          typeof value === "object" &&
          !Array.isArray(value) &&
          !systemFields.includes(key) &&
          !finalCategories.find((c) => c.key === key)
        ) {
          finalCategories.push({
            label: key.charAt(0).toUpperCase() + key.slice(1),
            key,
          });
          customCatKeys.add(key);
        }
      });

      const rootKeys = Object.keys(config).filter(
        (key) =>
          config[key] !== null &&
          !systemFields.includes(key) &&
          ![
            "firebase",
            "sendgrid",
            "yelp",
            "aws",
            "googleOauth",
            "dynamicFilters",
            "others",
          ].includes(key) &&
          !customCatKeys.has(key)
      );
      setOriginalKeys(new Set(rootKeys));

      // 2. Define all known fields for filtering generalInfo dynamically
      const allKnownFields = [
        ...systemFields,
        ...finalCategories.map((c) => c.key),
        "google_oauth_client_id",
        "google_oauth_client_secret",
        "google_oauth_redirect_uri",
        "seo_redirect_uris",
        ...othersFields,
        "clientId",
        "channel",
        "folderPath",
        "dbConnectionURL",
        "apiLink",
        "timeZone",
      ];

      const newFormData = {
        generalInfo: mapToDefaults("generalInfo", config, [
          { key: "clientId", value: "", isStatic: true, type: "string" },
          { key: "channel", value: "", isStatic: true, type: "string" },
          { key: "folderPath", value: "", isStatic: true, type: "string" },
          { key: "dbConnectionURL", value: "", isStatic: true, type: "string" },
          { key: "apiLink", value: "", isStatic: true, type: "string" },
          { key: "timeZone", value: "UTC", isStatic: true, type: "string" },
        ]).filter((f) => {
          const isDefault = [
            "clientId",
            "channel",
            "folderPath",
            "dbConnectionURL",
            "apiLink",
            "timeZone",
          ].includes(f.key);
          const isUnknown = !allKnownFields.includes(f.key);
          return isDefault || isUnknown;
        }),

        firebase: mapToDefaults("firebase", config.firebase || {}, [
          { key: "type", value: "", isStatic: true, type: "string" },
          { key: "project_id", value: "", isStatic: true, type: "string" },
          { key: "private_key_id", value: "", isStatic: true, type: "string" },
          { key: "private_key", value: "", isStatic: true, type: "string" },
          { key: "client_email", value: "", isStatic: true, type: "string" },
          { key: "client_id", value: "", isStatic: true, type: "string" },
          { key: "auth_uri", value: "", isStatic: true, type: "string" },
          { key: "token_uri", value: "", isStatic: true, type: "string" },
          { key: "auth_provider_x509_cert_url", value: "", isStatic: true, type: "string" },
          { key: "client_x509_cert_url", value: "", isStatic: true, type: "string" },
          { key: "universe_domain", value: "", isStatic: true, type: "string" },
        ]),
        sendgrid: mapToDefaults("sendgrid", config.sendgrid || {}, [
          { key: "api_key", value: "", isStatic: true, type: "string" },
          { key: "batchSize", value: "", isStatic: true, type: "number" },
          { key: "dailyLimit", value: "", isStatic: true, type: "number" },
        ]),
        yelp: mapToDefaults("yelp", config.yelp || {}, [
          { key: "api_key", value: "", isStatic: true, type: "string" },
        ]),
        aws: mapToDefaults("aws", config.aws || {}, [
          { key: "bucketName", value: "", isStatic: true, type: "string" },
          { key: "region", value: "", isStatic: true, type: "string" },
          { key: "domainUrl", value: "", isStatic: true, type: "string" },
        ]),
        googleOauth: mapToDefaults("googleOauth", config.googleOauth || {}, [
          { key: "google_oauth_client_id", value: "", isStatic: true, type: "string" },
          { key: "google_oauth_client_secret", value: "", isStatic: true, type: "string" },
          { key: "google_oauth_redirect_uri", value: "", isStatic: true, type: "string" },
          {
            key: "seo_redirect_uris",
            value: [],
            isStatic: true,
            type: "array",
          },
        ]),
        dynamicFilters: transformObjectToFields(config.dynamicFilters || {}),
        others: mapToDefaults("others", config.others || {}, [
          {
            key: "canCampaignSchedulerEnable",
            value: false,
            isStatic: true,
            type: "boolean",
          },
          {
            key: "canTokenRefreshEnable",
            value: false,
            isStatic: true,
            type: "boolean",
          },
          {
            key: "campaignSchedulerType",
            value: "node",
            isStatic: true,
            type: "string",
          },
        ]),
      };

      finalCategories.forEach((cat) => {
        if (!newFormData[cat.key]) {
          const value = config[cat.key] || {};
          newFormData[cat.key] = transformObjectToFields(value);
        }
      });

      setCategories(finalCategories);
      setFormData(newFormData);
      setOriginalCategories(customCatKeys);
    } catch (error) {
      console.error("Fetch client config failed", error);
      dispatch(messageShow("Failed to load client configuration"));
      history.goBack();
    }
  };

  const handleAddField = (category) => {
    setIsDirty(true);
    setFormData((prev) => ({
      ...prev,
      [category]: [
        ...(prev[category] || []),
        { key: "", value: "", type: "string" },
      ],
    }));
  };

  const handleAddCategory = () => {
    setIsCategoryDialogOpen(true);
    setNewCategoryName("");
  };

  const handleConfirmAddCategory = () => {
    const name = newCategoryName.trim();
    if (!name) return;

    const key = toCamelCase(name);
    if (categories.find((c) => c.key === key)) {
      dispatch(messageShow("Category already exists"));
      return;
    }

    setCategories((prev) => [...prev, { label: name, key }]);
    setFormData((prev) => ({ ...prev, [key]: [] }));
    setActiveTab(categories.length);
    setIsCategoryDialogOpen(false);
    setIsDirty(true);
  };

  const handleRemoveCategory = (e, index) => {
    e.stopPropagation();
    const categoryToRemove = categories[index];
    if (DEFAULT_CATEGORIES.find((c) => c.key === categoryToRemove.key)) return;

    if (
      window.confirm(
        `Are you sure you want to delete the "${categoryToRemove.label}" category and all its fields?`
      )
    ) {
      setIsDirty(true);
      setCategories((prev) => prev.filter((_, i) => i !== index));
      setFormData((prev) => {
        const newData = { ...prev };
        delete newData[categoryToRemove.key];
        return newData;
      });
      if (activeTab >= index) {
        setActiveTab(Math.max(0, index - 1));
      }
    }
  };

  const handleFieldUpdate = (category, index, newField) => {
    setIsDirty(true);
    setFormData((prev) => {
      const updatedCategory = [...prev[category]];
      updatedCategory[index] = newField;
      return { ...prev, [category]: updatedCategory };
    });
  };

  const handleCategoryUpdate = (category, newCategoryData) => {
    setIsDirty(true);
    setFormData((prev) => ({
      ...prev,
      [category]: newCategoryData,
    }));
  };

  const handleRemoveField = (category, index) => {
    setIsDirty(true);
    setFormData((prev) => ({
      ...prev,
      [category]: prev[category].filter((_, i) => i !== index),
    }));
  };

  const togglePasswordVisibility = (path) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [path]: !prev[path],
    }));
  };

  const buildPayload = () => {
    const payload = {
      crmId,
    };

    const reconstructObject = (fields) => {
      const obj = {};
      if (!Array.isArray(fields)) return obj;

      fields.forEach((f) => {
        if (!f.key) return;
        let val = f.value;
        if (f.type === "number") {
          val = Number(f.value);
        } else if (f.type === "object") {
          val = reconstructObject(f.value);
        } else if (f.type === "array") {
          val = Array.isArray(f.value)
            ? f.value
                .map((item) => (typeof item === "string" ? item.trim() : item))
                .filter((item) => item !== "" && item !== null && item !== undefined)
            : [];
        } else if (f.type === "boolean") {
          val = !!f.value;
        } else if (f.type === "date") {
          val = f.value ? new Date(f.value) : null;
        }

        obj[f.key] = val;
      });
      return obj;
    };

    if (formData.generalInfo) {
      formData.generalInfo.forEach((field) => {
        if (field.key) payload[field.key] = field.value;
      });
    }

    categories
      .filter((c) => c.key !== "generalInfo")
      .forEach((cat) => {
        const catKey = cat.key;
        const fields = formData[catKey] || [];

        const obj = reconstructObject(fields);
        
        // If empty object and not a default category, send null (to delete)?
        // Original logic checked length === 0.
        // Here we check keys.
        
        if (
          Object.keys(obj).length === 0 &&
          !DEFAULT_CATEGORIES.find((c) => c.key === catKey)
        ) {
          payload[catKey] = null;
          return;
        }

        payload[catKey] = obj;
      });

    originalCategories.forEach((key) => {
      if (!categories.find((c) => c.key === key)) {
        payload[key] = null;
      }
    });

    const currentKeys = new Set([
      ...(formData.generalInfo || []).map((f) => f.key),
    ]);

    originalKeys.forEach((key) => {
      if (!currentKeys.has(key)) {
        payload[key] = null;
      }
    });

    return payload;
  };

  const handleSave = async () => {
    try {
      const payload = buildPayload();
      let response;

      if (isEditMode) {
        payload._id = clientConfigId;
        response = await updateClientConfig(payload);
      } else {
        response = await createClientConfig(payload);
      }

      if (response?.data?.success) {
        setIsDirty(false);
        dispatch(
          messageShow(
            `Client configuration ${
              isEditMode ? "updated" : "created"
            } successfully`
          )
        );
        if (isModal && onSuccess) {
          onSuccess();
        } else {
          history.push(routes.clientConfig.path);
        }
      } else {
        dispatch(
          messageShow(
            response?.data?.message ||
              `Failed to ${isEditMode ? "update" : "create"} client config`
          )
        );
      }
    } catch (error) {
      console.error("client config Error", error);
      dispatch(messageShow("Something went wrong. Please try again later"));
    }
  };

  const isFormValid = () => {
    const mandatoryKeys = [
      "clientId",
      "channel",
      "folderPath",
      "dbConnectionURL",
      "apiLink",
      "timeZone",
    ];
    const generalFields = formData.generalInfo.reduce(
      (acc, f) => ({ ...acc, [f.key]: f.value }),
      {}
    );

    return mandatoryKeys.every(
      (key) => generalFields[key] && generalFields[key].toString().trim() !== ""
    );
  };

  const renderDynamicFields = (category) => {
    const fields = formData[category];

    if (category === "dynamicFilters") {
      return (
        <DynamicFiltersConfig 
          categoryFields={fields} 
          onCategoryUpdate={(newFields) => handleCategoryUpdate(category, newFields)} 
        />
      );
    }

    return (
      <Box className="tab-content">
        {fields.map((field, index) => (
          <DynamicField
            key={index}
            index={index}
            field={field}
            onChange={(newField) =>
              handleFieldUpdate(category, index, newField)
            }
            onRemove={() => handleRemoveField(category, index)}
            isEditMode={isEditMode}
            depth={0}
            path={`${category}.${index}`}
            togglePasswordVisibility={togglePasswordVisibility}
            passwordVisibility={passwordVisibility}
            categoryFields={fields}
            clientConfigId={clientConfigId}
            onStartScheduler={async () => {
              try {
                const response = await startClientScheduler(clientConfigId);
                if (response?.data?.success) {
                  dispatch(messageShow("Scheduler started successfully"));
                  fetchClientConfig();
                } else {
                  dispatch(messageShow(response?.data?.message || "Failed to start scheduler"));
                }
              } catch (err) {
                dispatch(messageShow("Error starting scheduler"));
              }
            }}
            onStopScheduler={async () => {
              try {
                const response = await stopClientScheduler(clientConfigId);
                if (response?.data?.success) {
                  dispatch(messageShow("Scheduler stopped successfully"));
                  fetchClientConfig();
                } else {
                  dispatch(messageShow(response?.data?.message || "Failed to stop scheduler"));
                }
              } catch (err) {
                dispatch(messageShow("Error stopping scheduler"));
              }
            }}
          />
        ))}

        <Button
          startIcon={<AddIcon />}
          onClick={() => handleAddField(category)}
          sx={{ mt: 1, textTransform: "none" }}
          color="primary"
        >
          Add Field
        </Button>
      </Box>
    );
  };

  return (
    <div
      className={
        isModal
          ? "create-or-update-container-modal"
          : "create-or-update-container"
      }
    >
      <Prompt
        when={isDirty}
        message="You have unsaved changes. Are you sure you want to leave without saving?"
      />
      {!isModal && (
        <Toolbar className="toolbar">
          <IconButton color="inherit" onClick={() => {
            if (isDirty && !window.confirm("You have unsaved changes. Are you sure you want to leave without saving?")) return;
            history.goBack();
          }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            color="inherit"
            className="grow"
            sx={{ ml: 1 }}
          >
            {isEditMode ? "Edit Client Config" : "Add Client Config"}
          </Typography>
        </Toolbar>
      )}

      <Box className="content-wrapper">
        <Box
          className="tabs-container"
          sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}
        >
          {categories.map((tab, index) => (
            <Box
              key={index}
              className="tab-item-wrapper"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <button
                className={`tab-button ${activeTab === index ? "active" : ""}`}
                onClick={() => setActiveTab(index)}
                type="button"
                style={{ position: "relative" }}
              >
                {tab.label}
                {!DEFAULT_CATEGORIES.find((c) => c.key === tab.key) && (
                  <IconButton
                    size="small"
                    className="tab-delete-icon"
                    onClick={(e) => handleRemoveCategory(e, index)}
                    sx={{ p: 0, ml: 1 }}
                  >
                    <DeleteIcon
                      fontSize="inherit"
                      style={{ fontSize: "14px" }}
                    />
                  </IconButton>
                )}
              </button>
            </Box>
          ))}
          <IconButton
            color="primary"
            onClick={handleAddCategory}
            sx={{ ml: 1, border: "1px dashed", borderRadius: "4px", p: "6px" }}
            title="Add New Category"
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <Box className="form-container">
            {categories[activeTab] &&
              renderDynamicFields(categories[activeTab].key)}
          </Box>

          <Box className="form-actions">
            {isModal && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  if (isDirty && !window.confirm("You have unsaved changes. Are you sure you want to leave without saving?")) return;
                  onClose();
                }}
                sx={{ mr: 2 }}
              >
                Close
              </Button>
            )}
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                if (isDirty && !window.confirm("You have unsaved changes. Are you sure you want to leave without saving?")) return;
                isModal ? onCancel() : history.goBack();
              }}
              sx={{ mr: 2 }}
            >
              {isModal ? "Back" : "Cancel"}
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!isFormValid()}
            >
              Save
            </Button>
          </Box>
        </form>
      </Box>

      <Dialog
        open={isCategoryDialogOpen}
        onClose={() => setIsCategoryDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Add New Category</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Category Name"
              placeholder="e.g. Analytics"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              autoFocus
              variant="outlined"
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setIsCategoryDialogOpen(false)}
            color="secondary"
            variant="outlined"
            size="small"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAddCategory}
            color="primary"
            variant="contained"
            size="small"
            disabled={!newCategoryName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CreateOrUpdate;
